import firebase from 'firebase/app';
import Vue from 'vue';
import { db } from '@/main';
import NOTESLIST_STRUCTURE_EVENTS from '@/lib/integration-events';

const STRUCTURE_STALE_MESSAGE = 'Structure was updated by someone else';

function resolveActIdForMarker(getters, pageIndex, type) {
    if (type === 'act') {
        return null;
    }
    const markers = getters.scriptMarkers || [];
    let best = null;
    for (const marker of markers) {
        if (marker.type === 'act') {
            const markerPage = Number(marker.pageIndex);
            if (markerPage <= Number(pageIndex)) {
                if (!best || markerPage > Number(best.pageIndex)) {
                    best = marker;
                }
            }
        }
    }
    return best ? (best.markerId || best.id) : null;
}

function resolveMarkerActId(getters, marker) {
    if (marker.type === 'act') {
        return null;
    }
    if (marker.actId != null) {
        return marker.actId;
    }
    return resolveActIdForMarker(getters, marker.pageIndex, marker.type);
}

function queueMarkerEvent(dispatch, payload) {
    return dispatch('queueNotesListStructureEvent', payload);
}

function markerUpdatedAtMs(value) {
    if (value == null) {
        return 0;
    }
    if (typeof value.toMillis === 'function') {
        return value.toMillis();
    }
    if (typeof value.seconds === 'number') {
        return (value.seconds * 1000) + Math.floor((value.nanoseconds || 0) / 1e6);
    }
    if (value instanceof Date) {
        return value.getTime();
    }
    const asNumber = Number(value);
    return Number.isFinite(asNumber) ? asNumber : 0;
}

function toastStructureConflict() {
    if (Vue.prototype.$toast) {
        Vue.prototype.$toast.warning(STRUCTURE_STALE_MESSAGE);
    }
}

/**
 * Reject updates/deletes when the live remote marker is newer than the client's
 * known updatedAt (or the doc was already deleted).
 */
async function assertMarkerWritable(projectRef, markerId, expectedUpdatedAt) {
    const snap = await projectRef.collection('scriptMarkers').doc(markerId).get();
    if (!snap.exists) {
        const err = new Error(STRUCTURE_STALE_MESSAGE);
        err.code = 'structure-stale';
        throw err;
    }
    if (expectedUpdatedAt == null) {
        return snap;
    }
    const remoteMs = markerUpdatedAtMs(snap.data().updatedAt);
    const localMs = markerUpdatedAtMs(expectedUpdatedAt);
    if (remoteMs > localMs) {
        const err = new Error(STRUCTURE_STALE_MESSAGE);
        err.code = 'structure-stale';
        throw err;
    }
    return snap;
}

function handleStructureWriteError(err) {
    if (err && (err.code === 'structure-stale' || err.message === STRUCTURE_STALE_MESSAGE)) {
        toastStructureConflict();
        return Promise.reject(err);
    }
    throw err;
}

const markerActions = {
    addScriptMarker({ getters, dispatch }, marker) {
        if (!getters.canEditStructure) {
            return Promise.reject(new Error('Not allowed to edit Structure'));
        }
        const projectRef = db.doc(getters.projectPath);
        const markerRef = projectRef.collection('scriptMarkers').doc();
        const actId = resolveMarkerActId(getters, marker);
        const payload = {
            markerId: markerRef.id,
            pageIndex: marker.pageIndex,
            x: marker.x || 0,
            y: marker.y || 0,
            type: marker.type,
            label: marker.label,
            actId: actId || null,
            createdAt: firebase.firestore.FieldValue.serverTimestamp(),
            updatedAt: firebase.firestore.FieldValue.serverTimestamp()
        };
        return markerRef.set(payload).then(() => {
            queueMarkerEvent(dispatch, {
                event: NOTESLIST_STRUCTURE_EVENTS.MARKER_CREATED,
                markerId: markerRef.id
            });
            return { id: markerRef.id, ...payload };
        });
    },

    updateScriptMarker({ getters, dispatch }, marker) {
        if (!getters.canEditStructure) {
            return Promise.reject(new Error('Not allowed to edit Structure'));
        }
        const projectRef = db.doc(getters.projectPath);
        const id = marker.markerId || marker.id;
        const actId = resolveMarkerActId(getters, marker);
        const expectedUpdatedAt = marker.updatedAt;
        return assertMarkerWritable(projectRef, id, expectedUpdatedAt)
            .then(() => projectRef.collection('scriptMarkers').doc(id).update({
                pageIndex: marker.pageIndex,
                x: marker.x,
                y: marker.y,
                type: marker.type,
                label: marker.label,
                actId: actId || null,
                updatedAt: firebase.firestore.FieldValue.serverTimestamp()
            }))
            .then(() => queueMarkerEvent(dispatch, {
                event: NOTESLIST_STRUCTURE_EVENTS.MARKER_UPDATED,
                markerId: id
            }))
            .catch(handleStructureWriteError);
    },

    deleteScriptMarker({ getters, dispatch }, markerOrId) {
        if (!getters.canEditStructure) {
            return Promise.reject(new Error('Not allowed to edit Structure'));
        }
        const projectRef = db.doc(getters.projectPath);
        const markers = getters.scriptMarkers || [];
        let markerId = markerOrId;
        let expectedUpdatedAt = null;
        let pageIndex = null;
        if (markerOrId && typeof markerOrId === 'object') {
            markerId = markerOrId.markerId || markerOrId.id;
            expectedUpdatedAt = markerOrId.updatedAt;
            pageIndex = markerOrId.pageIndex;
        }
        const marker = markers.find(m => (m.markerId || m.id) === markerId);
        if (marker) {
            if (expectedUpdatedAt == null) {
                expectedUpdatedAt = marker.updatedAt;
            }
            if (pageIndex == null) {
                pageIndex = marker.pageIndex;
            }
        }
        return assertMarkerWritable(projectRef, markerId, expectedUpdatedAt)
            .then(() => projectRef.collection('scriptMarkers').doc(markerId).delete())
            .then(() => queueMarkerEvent(dispatch, {
                event: NOTESLIST_STRUCTURE_EVENTS.MARKER_DELETED,
                markerId,
                pageIndex
            }))
            .catch(handleStructureWriteError);
    },

    async adjustScriptMarkersAfterPageDelete({ getters, dispatch }, { fromPage, toPage }) {
        const from = Number(fromPage);
        const to = Number(toPage);
        const deletedCount = (to - from) + 1;
        const markers = getters.scriptMarkers || [];
        if (!markers.length) {
            return;
        }
        const projectRef = db.doc(getters.projectPath);
        const batch = db.batch();
        for (const marker of markers) {
            const pageIndex = Number(marker.pageIndex);
            const id = marker.markerId || marker.id;
            const ref = projectRef.collection('scriptMarkers').doc(id);
            if (pageIndex >= from && pageIndex <= to) {
                batch.delete(ref);
            } else if (pageIndex > to) {
                batch.update(ref, {
                    pageIndex: pageIndex - deletedCount,
                    updatedAt: firebase.firestore.FieldValue.serverTimestamp()
                });
            }
        }
        await batch.commit();
        await queueMarkerEvent(dispatch, {
            event: NOTESLIST_STRUCTURE_EVENTS.STRUCTURE_FULL
        });
    },

    async adjustScriptMarkersAfterPageReplace(
        { getters, dispatch },
        { replaceFrom, replaceTo, newPageCount }
    ) {
        const from = Number(replaceFrom);
        const to = Number(replaceTo);
        const replacingPages = (to - from) + 1;
        const newCount = Number(newPageCount);
        const delta = newCount - replacingPages;
        if (!delta) {
            return;
        }
        const markers = getters.scriptMarkers || [];
        if (!markers.length) {
            return;
        }
        const projectRef = db.doc(getters.projectPath);
        const batch = db.batch();
        for (const marker of markers) {
            const pageIndex = Number(marker.pageIndex);
            const id = marker.markerId || marker.id;
            const ref = projectRef.collection('scriptMarkers').doc(id);
            if (delta < 0) {
                const deleteFrom = from + newCount;
                const deleteTo = to;
                if (pageIndex >= deleteFrom && pageIndex <= deleteTo) {
                    batch.delete(ref);
                } else if (pageIndex > to) {
                    batch.update(ref, {
                        pageIndex: pageIndex + delta,
                        updatedAt: firebase.firestore.FieldValue.serverTimestamp()
                    });
                }
            } else if (pageIndex > to) {
                batch.update(ref, {
                    pageIndex: pageIndex + delta,
                    updatedAt: firebase.firestore.FieldValue.serverTimestamp()
                });
            }
        }
        await batch.commit();
        await queueMarkerEvent(dispatch, {
            event: NOTESLIST_STRUCTURE_EVENTS.STRUCTURE_FULL
        });
    },

    async shiftScriptMarkersAfterPage({ getters, dispatch }, { fromPage, delta }) {
        const markers = (getters.scriptMarkers || []).filter(
            m => Number(m.pageIndex) >= Number(fromPage)
        );
        if (!markers.length) {
            return;
        }
        const projectRef = db.doc(getters.projectPath);
        const batch = db.batch();
        for (const marker of markers) {
            const id = marker.markerId || marker.id;
            batch.update(projectRef.collection('scriptMarkers').doc(id), {
                pageIndex: Number(marker.pageIndex) + Number(delta),
                // #386. Page ops wrote pageIndex alone, so they mutated markers
                // with no modification trail — every other write in this file
                // stamps updatedAt. It also left the optimistic-concurrency check
                // in updateScriptMarker blind to page ops, so a collaborator's
                // stale edit could overwrite a shift without raising the conflict
                // toast.
                updatedAt: firebase.firestore.FieldValue.serverTimestamp()
            });
        }
        await batch.commit();
        await queueMarkerEvent(dispatch, {
            event: NOTESLIST_STRUCTURE_EVENTS.STRUCTURE_FULL
        });
    }
};

export default markerActions;



// WEBPACK FOOTER //
// ./src/lib/vuex/marker-actions.js